import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronDown,
  Video,
  Link2,
  Check,
} from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

function SessionRow({ course, module, session, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    title: session.title,
    description: session.description || "",
    drive_link: session.drive_file_id ? `https://drive.google.com/file/d/${session.drive_file_id}/view` : "",
    duration: session.duration || "",
  });
  const [preview, setPreview] = useState(null);

  const save = async () => {
    try {
      await req("put", `/admin/courses/${course}/modules/${module}/sessions/${session.id}`, form);
      toast.success("Session saved");
      setEdit(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save session");
    }
  };

  const validate = async () => {
    try {
      const { data } = await req("post", "/admin/drive/validate", { link: form.drive_link });
      setPreview(data.embed_url);
      toast.success(`Valid Drive file: ${data.file_id}`);
    } catch (err) {
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Invalid Drive link");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete session "${session.title}"?`)) return;
    await req("delete", `/admin/courses/${course}/modules/${module}/sessions/${session.id}`);
    toast.success("Session deleted");
    onChanged();
  };

  if (!edit) {
    return (
      <li
        data-testid={`admin-session-${session.id}`}
        className="flex items-center gap-2 pl-10 pr-3 py-2 text-[13px] hover:bg-slate-50 dark:hover:bg-white/5"
      >
        {session.drive_file_id ? (
          <Video className="h-3.5 w-3.5 text-emerald-500 shrink-0" title="Video linked" />
        ) : (
          <Video className="h-3.5 w-3.5 text-slate-300 shrink-0" title="No video yet" />
        )}
        <span className="min-w-0 flex-1 truncate">{session.title}</span>
        <button type="button" onClick={() => setEdit(true)} data-testid={`edit-session-${session.id}`} className={btnGhost}>
          <Pencil className="h-3 w-3" /> Edit
        </button>
        <button type="button" onClick={remove} data-testid={`delete-session-${session.id}`} className={btnGhost}>
          <Trash2 className="h-3 w-3" />
        </button>
      </li>
    );
  }

  return (
    <li className="pl-10 pr-3 py-3 bg-slate-50 dark:bg-white/5 space-y-2">
      <input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Session title" data-testid={`session-title-input-${session.id}`} />
      <textarea rows={2} className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Session description" />
      <div className="flex gap-2">
        <input
          className={input}
          value={form.drive_link}
          onChange={(e) => setForm({ ...form, drive_link: e.target.value })}
          placeholder="https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
          data-testid={`session-drive-input-${session.id}`}
        />
        <button type="button" onClick={validate} data-testid={`session-drive-validate-${session.id}`} className={btnGhost}>
          <Link2 className="h-3 w-3" /> Preview
        </button>
      </div>
      <input className={input} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Duration e.g. 12 min" />
      {preview && (
        <iframe src={preview} title="preview" className="w-full aspect-video rounded-xl border border-slate-200 dark:border-white/10" allow="autoplay" />
      )}
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={save} data-testid={`save-session-${session.id}`} className={btnDark}>
          <Check className="h-3 w-3" /> Save session
        </button>
        <button type="button" onClick={() => setEdit(false)} className={btnGhost}>
          Cancel
        </button>
      </div>
    </li>
  );
}

function ModuleBlock({ slug, module, index, onChanged }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newLink, setNewLink] = useState("");

  const addSession = async () => {
    if (!newTitle.trim()) return;
    try {
      await req("post", `/admin/courses/${slug}/modules/${module.id}/sessions`, {
        title: newTitle,
        drive_link: newLink || null,
      });
      setNewTitle("");
      setNewLink("");
      setAdding(false);
      toast.success("Session added");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add session");
    }
  };

  const rename = async () => {
    const title = window.prompt("Module title", module.title);
    if (!title) return;
    await req("put", `/admin/courses/${slug}/modules/${module.id}`, { title });
    toast.success("Module renamed");
    onChanged();
  };

  const remove = async () => {
    if (!window.confirm(`Delete module "${module.title}" and all its sessions?`)) return;
    await req("delete", `/admin/courses/${slug}/modules/${module.id}`);
    toast.success("Module deleted");
    onChanged();
  };

  return (
    <div className="border-b border-slate-100 dark:border-white/5 last:border-0">
      <div className="flex items-center gap-2 px-3 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`admin-module-toggle-${index + 1}`}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
        >
          <span className="text-[10px] font-bold text-slate-400 w-6">{String(index + 1).padStart(2, "0")}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{module.title}</span>
          <span className="text-[11px] text-slate-400 shrink-0">{module.sessions?.length || 0} sessions</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button type="button" onClick={rename} className={btnGhost} data-testid={`rename-module-${index + 1}`}>
          <Pencil className="h-3 w-3" />
        </button>
        <button type="button" onClick={remove} className={btnGhost} data-testid={`delete-module-${index + 1}`}>
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {open && (
        <ul className="pb-3">
          {(module.sessions || []).map((s) => (
            <SessionRow key={s.id} course={slug} module={module.id} session={s} onChanged={onChanged} />
          ))}
          {adding ? (
            <li className="pl-10 pr-3 py-3 space-y-2 bg-slate-50 dark:bg-white/5">
              <input className={input} autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New session title" data-testid="new-session-title" />
              <input className={input} value={newLink} onChange={(e) => setNewLink(e.target.value)} placeholder="Google Drive link (optional)" data-testid="new-session-drive" />
              <div className="flex gap-2">
                <button type="button" onClick={addSession} className={btnDark} data-testid="new-session-save">
                  Add session
                </button>
                <button type="button" onClick={() => setAdding(false)} className={btnGhost}>
                  Cancel
                </button>
              </div>
            </li>
          ) : (
            <li className="pl-10 pr-3 pt-1">
              <button type="button" onClick={() => setAdding(true)} className={btnGhost} data-testid={`add-session-module-${index + 1}`}>
                <Plus className="h-3 w-3" /> Add session
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function CoursesManager() {
  const [list, setList] = useState([]);
  const [slug, setSlug] = useState(null);
  const [course, setCourse] = useState(null);
  const [coverUrl, setCoverUrl] = useState("");
  const [loading, setLoading] = useState(true);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await req("get", "/admin/courses");
      setList(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCourse = useCallback(async (s) => {
    const { data } = await req("get", `/admin/courses/${s}`);
    setCourse(data);
    setCoverUrl(data.cover || "");
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (slug) loadCourse(slug);
    else setCourse(null);
  }, [slug, loadCourse]);

  const createCourse = async () => {
    const title = window.prompt("New course title");
    if (!title) return;
    try {
      const { data } = await req("post", "/admin/courses", { title });
      toast.success("Course created");
      await loadList();
      setSlug(data.slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create course");
    }
  };

  const deleteCourse = async (s) => {
    if (!window.confirm(`Delete course "${s}"? This removes all its modules and videos.`)) return;
    await req("delete", `/admin/courses/${s}`);
    toast.success("Course deleted");
    setSlug(null);
    loadList();
  };

  const addModule = async () => {
    const title = window.prompt("Module title");
    if (!title) return;
    await req("post", `/admin/courses/${slug}/modules`, { title });
    toast.success("Module added");
    loadCourse(slug);
  };

  const saveMeta = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await req("put", `/admin/courses/${slug}`, {
        title: f.get("title"),
        short: f.get("short"),
        tagline: f.get("tagline"),
        overview: f.get("overview"),
        cover: coverUrl,
        category: f.get("category"),
        duration: f.get("duration"),
        level: f.get("level"),
        status: f.get("status"),
      });
      toast.success("Course updated");
      loadList();
      loadCourse(slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update course");
    }
  };

  if (!slug) {
    return (
      <div data-testid="admin-courses-manager" className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {list.length} courses. Edit modules, sessions and Google Drive videos.
          </p>
          <button type="button" onClick={createCourse} data-testid="admin-create-course" className={btnDark}>
            <Plus className="h-3.5 w-3.5" /> New course
          </button>
        </div>
        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden divide-y divide-slate-100 dark:divide-white/5">
          {loading && <div className="p-5 text-sm text-slate-400">Loading…</div>}
          {list.map((c) => (
            <div key={c.slug} data-testid={`admin-course-row-${c.slug}`} className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{c.title}</div>
                <div className="text-[11px] text-slate-400">
                  {c.module_count} modules · {c.session_count} sessions · {c.video_count} videos linked ·{" "}
                  <span className={c.status === "published" ? "text-emerald-500" : "text-amber-500"}>{c.status}</span>
                </div>
              </div>
              <button type="button" onClick={() => setSlug(c.slug)} data-testid={`admin-edit-course-${c.slug}`} className={btnDark}>
                <Pencil className="h-3 w-3" /> Manage
              </button>
              <button type="button" onClick={() => deleteCourse(c.slug)} data-testid={`admin-delete-course-${c.slug}`} className={btnGhost}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-course-editor" className="space-y-5">
      <button type="button" onClick={() => setSlug(null)} data-testid="admin-course-back" className={btnGhost}>
        <ChevronLeft className="h-3 w-3" /> All courses
      </button>

      {!course ? (
        <div className="text-sm text-slate-400">Loading course…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-5">
          <form onSubmit={saveMeta} className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3">
            <h3 className="font-display text-base font-semibold tracking-tight">Course settings</h3>
            <input name="title" className={input} defaultValue={course.title} placeholder="Course title" data-testid="course-title-input" />
            <input name="tagline" className={input} defaultValue={course.tagline} placeholder="Tagline" />
            <input name="short" className={input} defaultValue={course.short} placeholder="Short description" />
            <textarea name="overview" rows={3} className={input} defaultValue={course.overview} placeholder="Full description" />
            <ImageUpload
              value={coverUrl}
              onChange={setCoverUrl}
              folder="covers"
              label="Course cover photo"
              testid="course-cover-upload"
            />
            <input
              name="cover_url"
              className={input}
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="…or paste an image URL"
              data-testid="course-cover-url"
            />
            <div className="grid grid-cols-2 gap-3">
              <input name="category" className={input} defaultValue={course.category} placeholder="Category" />
              <select name="status" className={input} defaultValue={course.status} data-testid="course-status-select">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <input name="duration" className={input} defaultValue={course.duration} placeholder="Duration" />
              <input name="level" className={input} defaultValue={course.level} placeholder="Level" />
            </div>
            <button type="submit" className={btnDark} data-testid="course-save-meta">
              <Check className="h-3 w-3" /> Save course
            </button>
          </form>

          <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 dark:border-white/5">
              <h3 className="font-display text-base font-semibold tracking-tight">
                Modules & videos
              </h3>
              <button type="button" onClick={addModule} className={btnDark} data-testid="admin-add-module">
                <Plus className="h-3 w-3" /> Add module
              </button>
            </div>
            <div>
              {(course.modules || []).map((m, i) => (
                <ModuleBlock key={m.id} slug={slug} module={m} index={i} onChanged={() => loadCourse(slug)} />
              ))}
              {(course.modules || []).length === 0 && (
                <div className="p-5 text-sm text-slate-400">No modules yet — add your first one.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
